import { MapPin } from "lucide-react";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from "react-simple-maps";

/** US Counties TopoJSON — public CDN. */
const COUNTIES_URL =
  "https://cdn.jsdelivr.net/npm/us-atlas@3/counties-10m.json";

/** Washington DC — center of the no-fee circle. */
const DC_CENTER = [-77.0369, 38.9072];

/**
 * Radius of the no-fee circle in SVG pixels.
 * At geoMercator scale 18000 centered on DC, ~125px reaches down to
 * Woodbridge, VA (~20 miles south of DC) — sets the outer edge of the
 * immediate/no-fee service area.
 */
const NO_FEE_RADIUS_PX = 125;

/** Notable cities inside the no-fee circle. */
const INNER_CITIES = [
  { name: "Washington DC", coords: [-77.0369, 38.9072], size: "lg" },
  { name: "Arlington", coords: [-77.109, 38.879] },
  { name: "Alexandria", coords: [-77.047, 38.805] },
  { name: "Bethesda", coords: [-77.106, 38.984] },
  { name: "Silver Spring", coords: [-77.02, 38.99] },
  { name: "Tysons", coords: [-77.222, 38.919] },
  { name: "Fairfax", coords: [-77.306, 38.846] },
];

/** Outer cities — informational only, all +10% travel fee. */
const OUTER_CITIES = [
  { name: "Frederick", coords: [-77.409, 39.414] },
  { name: "Baltimore", coords: [-76.612, 39.29] },
  { name: "Annapolis", coords: [-76.492, 38.9784] },
  { name: "Fredericksburg", coords: [-77.486, 38.302] },
];

// Mercator projection centered on the DC region, tightly framed
const projectionConfig = {
  center: [-77.2, 38.85],
  scale: 18000,
};

export default function ServiceMap() {
  return (
    <section
      id="service-area"
      className="py-24 lg:py-32 px-6 lg:px-12 bg-[#FAF7F2] relative overflow-hidden"
      data-testid="service-area-section"
    >
      <div className="mx-auto max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          {/* Copy */}
          <div className="lg:col-span-5">
            <span className="inline-block bg-white border border-stone-200 px-3 py-1.5 rounded-full text-xs font-semibold text-stone-700 mb-5">
              04 — Service area
            </span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
              We cover the whole
              <br />
              <span className="italic font-medium text-teal-700">
                DC region.
              </span>
            </h2>
            <p className="mt-5 text-stone-600 text-base leading-relaxed">
              On-site measure &amp; drafting across the immediate DC region at
              standard rates. Anywhere outside that circle across Maryland
              &amp; Virginia? We still come — a flat{" "}
              <strong>10% travel fee</strong> covers the extra drive.
            </p>

            <div className="mt-6 space-y-3" data-testid="service-area-legend">
              <div className="flex items-start gap-3">
                <span className="mt-1 h-4 w-4 rounded-full bg-teal-400 ring-4 ring-teal-100 flex-shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-stone-900">
                    Immediate DC region — no travel fee
                  </div>
                  <div className="text-xs text-stone-500 mt-0.5">
                    DC and everything inside the Beltway — Arlington,
                    Alexandria, Bethesda, Silver Spring, Tysons, Fairfax
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="mt-1 h-4 w-4 rounded-full bg-amber-400 ring-4 ring-amber-100 flex-shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-stone-900">
                    Everywhere else in MD &amp; VA — flat 10% travel fee
                  </div>
                  <div className="text-xs text-stone-500 mt-0.5">
                    Frederick · Baltimore · Annapolis · Loudoun · Prince
                    William · Fredericksburg &amp; anywhere in between
                  </div>
                </div>
              </div>
            </div>

            <p className="mt-6 text-xs italic text-stone-500">
              *We&apos;ll travel any distance — for anything further out
              we&apos;ll confirm the fee before booking.*
            </p>
          </div>

          {/* Map */}
          <div className="lg:col-span-7 relative" data-testid="service-area-map">
            <div className="relative bg-white border border-stone-200 rounded-[2rem] p-4 lg:p-6 shadow-sm">
              <ComposableMap
                projection="geoMercator"
                projectionConfig={projectionConfig}
                width={800}
                height={600}
                style={{ width: "100%", height: "auto" }}
                data-testid="service-area-svg"
              >
                {/* Base layer: every MD / VA / DC county filled amber = +10% area */}
                <Geographies geography={COUNTIES_URL}>
                  {({ geographies }) =>
                    geographies
                      .filter((geo) => {
                        const stateFips = geo.id?.substring(0, 2);
                        return ["11", "24", "51"].includes(stateFips);
                      })
                      .map((geo) => (
                        <Geography
                          key={geo.id}
                          geography={geo}
                          fill="#fcd34d"
                          stroke="#b45309"
                          strokeWidth={0.5}
                          style={{
                            default: { outline: "none" },
                            hover: {
                              outline: "none",
                              fill: "#fbbf24",
                              cursor: "default",
                            },
                            pressed: { outline: "none" },
                          }}
                        />
                      ))
                  }
                </Geographies>

                {/* No-fee zone: filled teal circle centered on DC */}
                <Marker coordinates={DC_CENTER}>
                  <circle
                    r={NO_FEE_RADIUS_PX}
                    fill="#5eead4"
                    fillOpacity={0.85}
                    stroke="#0d9488"
                    strokeWidth={2}
                    strokeDasharray="6 4"
                    data-testid="service-area-no-fee-circle"
                  />
                </Marker>

                {/* Inner-Beltway city markers */}
                {INNER_CITIES.map((c) => (
                  <Marker key={c.name} coordinates={c.coords}>
                    <circle
                      r={c.size === "lg" ? 5 : 3}
                      fill="#0f766e"
                      stroke="#fff"
                      strokeWidth={1.2}
                    />
                    <text
                      x={7}
                      y={4}
                      fontSize={c.size === "lg" ? 12 : 10}
                      fontWeight={c.size === "lg" ? 800 : 700}
                      fill="#134e4a"
                      style={{
                        paintOrder: "stroke",
                        stroke: "#fff",
                        strokeWidth: 3,
                        strokeLinejoin: "round",
                      }}
                    >
                      {c.name}
                    </text>
                  </Marker>
                ))}

                {/* Outer city markers */}
                {OUTER_CITIES.map((c) => (
                  <Marker key={c.name} coordinates={c.coords}>
                    <circle
                      r={4}
                      fill="#b45309"
                      stroke="#fff"
                      strokeWidth={1.2}
                    />
                    <text
                      x={7}
                      y={3}
                      fontSize={11}
                      fontWeight={700}
                      fill="#78350f"
                      style={{
                        paintOrder: "stroke",
                        stroke: "#fff",
                        strokeWidth: 3,
                        strokeLinejoin: "round",
                      }}
                    >
                      {c.name}
                    </text>
                    <text
                      x={7}
                      y={16}
                      fontSize={9}
                      fontWeight={700}
                      fill="#b45309"
                      style={{
                        paintOrder: "stroke",
                        stroke: "#fff",
                        strokeWidth: 3,
                        strokeLinejoin: "round",
                      }}
                    >
                      +10%
                    </text>
                  </Marker>
                ))}
              </ComposableMap>

              {/* Bottom info strip */}
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-stone-100">
                <div className="inline-flex items-center gap-2 text-xs font-semibold text-stone-600">
                  <MapPin className="h-3.5 w-3.5 text-teal-700" />
                  Locally owned · we drive out from Northern VA every day
                </div>
                <div className="flex flex-wrap items-center gap-3 text-xs">
                  <span className="inline-flex items-center gap-1.5 font-semibold text-teal-800">
                    <span className="h-3 w-3 rounded-full bg-teal-300 border border-teal-600" />
                    Inside the circle · no fee
                  </span>
                  <span className="inline-flex items-center gap-1.5 font-semibold text-amber-900">
                    <span className="h-3 w-3 rounded-sm bg-amber-300 border border-amber-600" />
                    Outside · +10% travel
                  </span>
                  <a
                    href="mailto:hello@dmvfp.com?subject=Service%20area%20question"
                    data-testid="service-area-cta"
                    className="font-semibold text-teal-700 hover:text-stone-900"
                  >
                    Outside these? Ask →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
